/**
 * API client hook.
 */

import { useAuthRuntime } from "@frontend/components/auth/auth-runtime"
import { ApiError, type ApiErrorBody } from "@frontend/lib/api-error"
import {
  type UseMutationOptions,
  type UseQueryOptions,
  useMutation,
  useQuery,
  useQueryClient
} from "@tanstack/react-query"
import { useCallback } from "react"

/**
 * Returns an async function for making authenticated API calls.
 * Prepends /api to the path and attaches Bearer token when available.
 */
export function useApiClient() {
  const { getAccessToken, isAuthenticated, logout } = useAuthRuntime()

  return useCallback(
    async (path: string, options?: RequestInit): Promise<any> => {
      const headers = new Headers(options?.headers)

      if (!headers.has("Authorization")) {
        const token = await getAccessToken()
        if (token) {
          headers.set("Authorization", `Bearer ${token}`)
        } else if (isAuthenticated) {
          // Token refresh failed for an authenticated user — session expired
          logout()
          return new Promise(() => {}) // Never resolves; page is navigating away
        }
      }

      if (!headers.has("Content-Type") && options?.body && !(options.body instanceof FormData)) {
        headers.set("Content-Type", "application/json")
      }

      const res = await fetch(`/api${path}`, {
        ...options,
        headers
      })

      if (res.status === 401) {
        logout()
        return new Promise(() => {}) // Never resolves; page is navigating away
      }

      if (!res.ok) {
        const body = (await res.json().catch(() => ({}) as ApiErrorBody)) as ApiErrorBody
        throw new ApiError(res.status, body)
      }

      return res.json()
    },
    [getAccessToken, isAuthenticated, logout]
  )
}

/**
 * Typed query hook wrapping TanStack Query's useQuery with the API client.
 */
export function useApiQuery<T = any>(
  key: string[],
  path: string,
  options?: Omit<UseQueryOptions<T>, "queryKey" | "queryFn">
) {
  const api = useApiClient()
  return useQuery<T>({
    queryKey: key,
    queryFn: () => api(path),
    ...options
  })
}

/**
 * Typed mutation hook wrapping TanStack Query's useMutation with the API client.
 */
export function useApiMutation<TData = any, TVariables = any>(
  path: string,
  options?: Omit<UseMutationOptions<TData, Error, TVariables>, "mutationFn"> & {
    method?: string
    invalidateKeys?: string[][]
  }
) {
  const api = useApiClient()
  const queryClient = useQueryClient()
  const { method = "POST", invalidateKeys, ...mutationOptions } = options ?? {}

  return useMutation<TData, Error, TVariables>({
    mutationFn: (variables: TVariables) =>
      api(path, {
        method,
        body: JSON.stringify(variables)
      }),
    onSuccess: (...args) => {
      // Invalidate related queries
      if (invalidateKeys) {
        for (const key of invalidateKeys) {
          queryClient.invalidateQueries({ queryKey: key })
        }
      }
      mutationOptions.onSuccess?.(...args)
    },
    ...mutationOptions
  })
}
